import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Sparkles, TrendingUp, Target, DollarSign, Users, AlertCircle, CheckCircle } from 'lucide-react';
import Header from '../../components/ui/Header';
import InquirySelector from './components/InquirySelector';
import InquiryAnalysisPanel from './components/InquiryAnalysisPanel';
import RecommendationCard from './components/RecommendationCard';
import { analyzeInquiryAndRecommendPlayers, fetchTransferInquiries } from '../../services/aiMatchRecommendationService';

const AIMatchRecommendationEngine = () => {
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingInquiries, setLoadingInquiries] = useState(true);

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    setLoadingInquiries(true);
    const { inquiries: data, error: err } = await fetchTransferInquiries();
    if (err) {
      setError(err);
    } else {
      setInquiries(data);
      if (data?.length > 0) {
        setSelectedInquiry(data?.[0]);
      }
    }
    setLoadingInquiries(false);
  };

  const handleAnalyzeInquiry = async (inquiry) => {
    if (!inquiry) return;
    
    setLoading(true);
    setError(null);
    setRecommendations([]);
    setAnalysis(null);

    const result = await analyzeInquiryAndRecommendPlayers(inquiry);
    
    if (result?.error) {
      setError(result?.error);
    } else {
      setRecommendations(result?.recommendations || []);
      setAnalysis(result?.analysis);
    }
    
    setLoading(false);
  };

  const handleInquirySelect = (inquiry) => {
    setSelectedInquiry(inquiry);
    setRecommendations([]);
    setAnalysis(null);
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>AI Match Recommendation Engine - ProX11 Market</title>
        <meta name="description" content="Intelligent player-club matching powered by AI. Analyze scout inquiries and get personalized talent recommendations." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Header />

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-10 h-10" />
              <h1 className="text-4xl font-bold">AI Match Recommendation Engine</h1>
            </div>
            <p className="text-center text-lg text-indigo-100 max-w-3xl mx-auto">
              Intelligent player-club matching powered by OpenAI. Analyze scout inquiries and discover optimal talent fits based on requirements, budget, and playing style.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          {/* Inquiry Selector */}
          <InquirySelector
            inquiries={inquiries}
            selectedInquiry={selectedInquiry}
            onSelect={handleInquirySelect}
            loading={loadingInquiries}
          />

          {/* Action Button */}
          {selectedInquiry && (
            <div className="flex justify-center mb-8">
              <button
                onClick={() => handleAnalyzeInquiry(selectedInquiry)}
                disabled={loading}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
              >
                <Sparkles className="w-6 h-6" />
                {loading ? 'Analyzing with AI...' : 'Generate AI Recommendations'}
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-red-900 font-semibold mb-1">Analysis Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                <Sparkles className="w-8 h-8 text-indigo-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-gray-600 mt-6 text-lg font-medium">AI is analyzing the inquiry and matching players...</p>
              <p className="text-gray-500 mt-2">This may take a few moments</p>
            </div>
          )}

          {/* Results Section */}
          {!loading && (analysis || recommendations?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Panel - Inquiry Analysis */}
              <div className="lg:col-span-1">
                <InquiryAnalysisPanel analysis={analysis} inquiry={selectedInquiry} />
              </div>

              {/* Right Panel - Player Recommendations */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Target className="w-6 h-6 text-indigo-600" />
                      <h2 className="text-2xl font-bold text-gray-900">AI-Generated Recommendations</h2>
                    </div>
                    <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {recommendations?.length} Matches Found
                    </div>
                  </div>

                  {recommendations?.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No player recommendations available</p>
                      <p className="text-gray-400 mt-2">Try analyzing an inquiry to see AI-powered matches</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {recommendations?.map((recommendation, index) => (
                        <RecommendationCard
                          key={recommendation?.playerId || index}
                          recommendation={recommendation}
                          rank={index + 1}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !analysis && recommendations?.length === 0 && !error && selectedInquiry && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Find Perfect Matches</h3>
              <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
                Click "Generate AI Recommendations" to analyze the selected inquiry and discover the best player-club matches powered by advanced AI algorithms.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl">
                  <Target className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Smart Matching</h4>
                  <p className="text-gray-600 text-sm">AI analyzes position, skills, and playing style</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl">
                  <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Performance Insights</h4>
                  <p className="text-gray-600 text-sm">Evaluates stats, experience, and potential</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl">
                  <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Budget Alignment</h4>
                  <p className="text-gray-600 text-sm">Matches players within your budget range</p>
                </div>
              </div>
            </div>
          )}

          {/* No Inquiries State */}
          {!loadingInquiries && inquiries?.length === 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Active Inquiries</h3>
              <p className="text-gray-600 text-lg">There are currently no open transfer inquiries to analyze.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIMatchRecommendationEngine;